(() => {
  const ALGOLIA_APP_ID = "RVBE1UCZU9";
  const ALGOLIA_SEARCH_API_KEY = "e37d703f9256b4f42b7dc520dca86cce";
  const ALGOLIA_INDEX_NAME = "shopify_products_manual_index_prod";
  const ALGOLIA_MULTI_QUERY_ENDPOINT = `https://${ALGOLIA_APP_ID}-dsn.algolia.net/1/indexes/*/queries`;
  const RETURN_TARGET_KEY = "dpp:fitment-return-target";
  const RETURN_TARGET_TTL = 60 * 1000;

  class FitmentCollectionRedesign {
    constructor(root) {
      this.root = root;
      this.categoryNavigation = null;
      this.panels = [...root.querySelectorAll("[data-fitment-category-panel]")];
      this.stickyOffsetFrame = null;
      this.headerResizeObserver = null;
      this.headerMutationObserver = null;
      this.revealObserver = null;
      this.toggleHoverAnimations = new Map();
      this.panelTransitions = new Map();
      this.panelPositionToken = 0;
      this.panelPositionTarget = null;
      this.panelScrollFrame = null;
      this.mobilePanelSwitchToken = 0;
      this.mobilePanelEntryTimer = null;
      this.panelRevealTokens = new WeakMap();
      this.imageDecodePromises = new WeakMap();
      this.returnTarget = this.consumeReturnTarget();
      this.init();
    }

    init() {
      this.ensureBreadcrumb();
      this.bindCategoryNavigation();
      this.setInitialPanelState();
      this.bindPanels();
      this.bindPanelImagePreloading();
      this.bindToggleHoverMotion();
      this.bindStickyNavOffset();
      this.bindScrollReveal();
      this.bindReturnTargetRestore();
      this.filterEmptySubtypeShortcuts();
      this.scrollToInitialHashPanel();
    }

    ensureBreadcrumb() {
      const headingRow = this.root.querySelector(
        ".fitment-category-layout__heading-row",
      );
      if (
        !headingRow ||
        headingRow.querySelector(".fitment-category-layout__breadcrumb")
      ) {
        return;
      }

      const breadcrumb = document.createElement("nav");
      breadcrumb.className = "fitment-category-layout__breadcrumb";
      breadcrumb.setAttribute("aria-label", "Breadcrumb");

      const home = document.createElement("a");
      home.href = window.Shopify?.routes?.root || "/";
      home.textContent = "Home";

      const separator = document.createElement("span");
      separator.setAttribute("aria-hidden", "true");
      separator.textContent = "/";

      const current = document.createElement("span");
      current.className = "fitment-category-layout__breadcrumb-current";
      current.setAttribute("aria-current", "page");
      current.textContent =
        this.root.dataset.currentCollectionTitle || document.title;

      breadcrumb.append(home, separator, current);
      headingRow.append(breadcrumb);
    }

    setInitialPanelState() {
      const requestedPanelId =
        this.returnTarget?.targetId ||
        decodeURIComponent(window.location.hash.replace(/^#/, ""));
      const requestedPanel = this.findPanel(requestedPanelId);
      const openAllCategories =
        !requestedPanel &&
        this.isTruthyFlag(this.root.dataset.fitmentOpenAllCategories);
      const activePanel =
        requestedPanel ||
        this.panels.find((panel) => panel.open) ||
        this.panels[0];

      this.initialHashPanel = requestedPanel;

      this.panels.forEach((panel) => {
        panel.open = openAllCategories || panel === activePanel;
        panel.classList.toggle("is-active", panel === activePanel);
      });

      this.updatePanelHeights();

      if (activePanel?.id) {
        this.setActiveNav(activePanel.id);
      }
    }

    bindReturnTargetRestore() {
      window.addEventListener("pageshow", (event) => {
        if (!event.persisted) return;

        const returnTarget = this.consumeReturnTarget();
        const panel = returnTarget
          ? this.findPanel(returnTarget.targetId)
          : null;
        if (!panel) return;

        this.openPanel(panel);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.scrollToPanel(panel));
        });
      });
    }

    consumeReturnTarget() {
      try {
        const stored = window.sessionStorage.getItem(RETURN_TARGET_KEY);
        if (!stored) return null;

        window.sessionStorage.removeItem(RETURN_TARGET_KEY);
        const target = JSON.parse(stored);
        const isCurrentPage = target.pathname === window.location.pathname;
        const isFresh =
          Date.now() - Number(target.createdAt || 0) < RETURN_TARGET_TTL;
        if (!isCurrentPage || !isFresh || !target.targetId) {
          return null;
        }

        return target;
      } catch (_error) {
        return null;
      }
    }

    scrollToInitialHashPanel() {
      if (!this.initialHashPanel) return;

      const scrollToRequestedPanel = () => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.scrollToPanel(this.initialHashPanel);
          });
        });
      };

      if (document.readyState === "complete") {
        scrollToRequestedPanel();
      } else {
        window.addEventListener("load", scrollToRequestedPanel, { once: true });
      }
    }

    bindCategoryNavigation() {
      const Navigation = window.DPPFitmentCategoryNavigation;
      if (!Navigation) return;

      this.categoryNavigation = new Navigation(this.root, {
        onSelect: (panelId) => {
          const target = this.findPanel(panelId);
          if (!target) return;
          this.openPanel(target, false, false);
          window.requestAnimationFrame(() => {
            this.openPanel(target, false, false);
            this.scrollToPanel(target);
          });
        },
      });
    }

    bindPanels() {
      this.panels.forEach((panel) => {
        const summary = panel.querySelector("summary");
        summary?.addEventListener("click", (event) => {
          event.preventDefault();
          this.finishPanelPositioning();
          const panelTop = panel.getBoundingClientRect().top;
          const shouldOpen =
            !panel.open || panel.classList.contains("is-closing");

          if (shouldOpen) {
            if (this.usesMobileAtomicPanelSwitch()) {
              this.openMobilePanel(panel);
              this.triggerPanelIconAnimation(panel);
              return;
            }

            const collapsingPanels = this.panels.filter((candidate) => {
              if (candidate === panel) return false;

              const isOpen =
                candidate.open || candidate.classList.contains("is-opening");
              const isBeforePanel = Boolean(
                candidate.compareDocumentPosition(panel) &
                  Node.DOCUMENT_POSITION_FOLLOWING,
              );

              return isOpen && isBeforePanel;
            });
            const collapsingHeight = collapsingPanels.reduce(
              (total, candidate) => {
                const content = candidate.querySelector(
                  "[data-fitment-panel-content]",
                );
                return total + (content?.getBoundingClientRect().height || 0);
              },
              0,
            );
            const panelTransition = this.openPanel(panel);
            this.triggerPanelIconAnimation(panel);
            this.preservePanelPosition(
              panel,
              panelTop,
              collapsingHeight,
              panelTransition,
            );
          } else {
            if (this.usesMobileAtomicPanelSwitch()) {
              this.mobilePanelSwitchToken += 1;
              this.clearMobilePanelVisualState();
            }
            this.closePanel(panel);
          }
        });
      });
    }

    usesMobileAtomicPanelSwitch() {
      return window.matchMedia("(max-width: 749px)").matches;
    }

    openMobilePanel(panel) {
      const switchToken = ++this.mobilePanelSwitchToken;
      const scrollingElement =
        document.scrollingElement || document.documentElement;
      const documentElement = document.documentElement;
      const body = document.body;

      this.clearMobilePanelVisualState();
      this.root.classList.add("is-mobile-atomic-switch");
      documentElement.classList.add("is-fitment-mobile-panel-switch");
      body?.classList.add("is-fitment-mobile-panel-switch");

      // Change both details states and calculate the final layout in the same
      // click task. Safari therefore has no intermediate collapsed/open frame
      // to paint before the single scroll-position correction below.
      this.openPanel(panel, false, false);
      const targetScrollY = this.getPanelScrollTarget(panel);
      scrollingElement.scrollTop = targetScrollY;
      panel.classList.add("is-mobile-panel-entering");

      window.requestAnimationFrame(() => {
        if (switchToken !== this.mobilePanelSwitchToken) return;
        this.root.classList.remove("is-mobile-atomic-switch");
        documentElement.classList.remove("is-fitment-mobile-panel-switch");
        body?.classList.remove("is-fitment-mobile-panel-switch");
        this.updateStickyNavOffset();
      });

      this.mobilePanelEntryTimer = window.setTimeout(() => {
        if (switchToken !== this.mobilePanelSwitchToken) return;
        panel.classList.remove("is-mobile-panel-entering");
        this.mobilePanelEntryTimer = null;
      }, 240);
    }

    clearMobilePanelVisualState() {
      window.clearTimeout(this.mobilePanelEntryTimer);
      this.mobilePanelEntryTimer = null;
      this.panels.forEach((candidate) =>
        candidate.classList.remove("is-mobile-panel-entering"),
      );
      this.root.classList.remove("is-mobile-atomic-switch");
      document.documentElement.classList.remove(
        "is-fitment-mobile-panel-switch",
      );
      document.body?.classList.remove("is-fitment-mobile-panel-switch");
    }

    bindPanelImagePreloading() {
      const preloadAfterPagePaint = () => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            const subcategoryImages = [
              ...this.root.querySelectorAll(
                ".fitment-subcategory-card__image img",
              ),
            ];

            this.prepareImages(subcategoryImages, "low", true);
          });
        });
      };

      if (document.readyState === "complete") {
        preloadAfterPagePaint();
      } else {
        window.addEventListener("load", preloadAfterPagePaint, { once: true });
      }
    }

    async filterEmptySubtypeShortcuts() {
      const cards = [
        ...this.root.querySelectorAll(
          "[data-fitment-subtype-shortcut][data-collection-id]",
        ),
      ];
      if (!cards.length) return;

      const requests = cards.map((card) => {
        const collectionId = card.dataset.collectionId;
        const subtype = this.escapeAlgoliaFilterValue(
          card.dataset.fitmentSubtypeShortcut,
        );
        const params = new URLSearchParams({
          query: "",
          hitsPerPage: "0",
          filters: [
            "status:ACTIVE",
            "NOT metafield_seo_hidden:1",
            `collection_ids:${collectionId}`,
            `metafield_product_subtype:"${subtype}"`,
          ].join(" AND "),
        });

        return {
          indexName: ALGOLIA_INDEX_NAME,
          params: params.toString(),
        };
      });

      try {
        const response = await fetch(ALGOLIA_MULTI_QUERY_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Algolia-API-Key": ALGOLIA_SEARCH_API_KEY,
            "X-Algolia-Application-Id": ALGOLIA_APP_ID,
          },
          body: JSON.stringify({ requests }),
        });
        if (!response.ok) {
          throw new Error(`Subtype count request failed: ${response.status}`);
        }

        const payload = await response.json();
        const affectedPanels = new Set();
        cards.forEach((card, index) => {
          const panel = card.closest("[data-fitment-category-panel]");
          if (panel) affectedPanels.add(panel);

          const count = Number(payload.results?.[index]?.nbHits || 0);
          if (count === 0) card.remove();
        });

        this.updateSubcategoryCounts(affectedPanels);
        this.updatePanelHeights();
      } catch (error) {
        console.warn("[DPP] Failed to check fitment subtype availability", error);
      }
    }

    updateSubcategoryCounts(panels = this.panels) {
      panels.forEach((panel) => {
        const count = panel.querySelectorAll(
          ".fitment-subcategory-grid > .fitment-subcategory-card",
        ).length;
        const countElement = panel.querySelector(
          ".fitment-category-panel__count",
        );
        if (countElement) {
          countElement.textContent = `${count} ${
            count === 1 ? "subcategory" : "subcategories"
          }`;
        }
      });
    }

    bindToggleHoverMotion() {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }

      this.panels.forEach((panel) => {
        const summary = panel.querySelector(".fitment-category-panel__summary");
        const toggle = panel.querySelector(".fitment-category-panel__toggle");
        if (!summary || !toggle) return;

        summary.addEventListener("pointerenter", () => {
          const existingAnimation = this.toggleHoverAnimations.get(toggle);
          const startTransform = getComputedStyle(toggle).transform;
          existingAnimation?.cancel();

          const distance = panel.open ? -3 : 3;
          const animation = toggle.animate(
            [
              { transform: startTransform },
              { transform: `translateY(${distance}px)` },
              { transform: "translateY(0)" },
            ],
            {
              duration: 720,
              iterations: Infinity,
              easing: "ease-in-out",
            },
          );

          this.toggleHoverAnimations.set(toggle, animation);
        });

        summary.addEventListener("pointerleave", () => {
          const activeAnimation = this.toggleHoverAnimations.get(toggle);
          if (!activeAnimation) return;

          const currentTransform = getComputedStyle(toggle).transform;
          activeAnimation.cancel();

          const settleAnimation = toggle.animate(
            [{ transform: currentTransform }, { transform: "translateY(0)" }],
            {
              duration: 220,
              easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            },
          );

          this.toggleHoverAnimations.set(toggle, settleAnimation);
          settleAnimation.addEventListener(
            "finish",
            () => {
              if (this.toggleHoverAnimations.get(toggle) === settleAnimation) {
                this.toggleHoverAnimations.delete(toggle);
              }
            },
            { once: true },
          );
        });
      });
    }

    openPanel(activePanel, shouldScroll = false, shouldAnimate = true) {
      const shouldReplayReveals =
        !activePanel.open || activePanel.classList.contains("is-closing");
      const panelTransitions = [];

      this.panels.forEach((panel) => {
        if (panel !== activePanel) {
          if (shouldAnimate) {
            panelTransitions.push(this.closePanel(panel));
          } else {
            panelTransitions.push(this.setPanelOpenImmediately(panel, false));
          }
        }
        panel.classList.toggle("is-active", panel === activePanel);
        panel
          .querySelector(".fitment-category-panel__icon")
          ?.classList.toggle("is-active", panel === activePanel);
      });

      const panelTransition = shouldAnimate
        ? this.animatePanel(activePanel, true)
        : this.setPanelOpenImmediately(activePanel, true);
      panelTransitions.push(panelTransition);
      activePanel.classList.add("is-active");
      this.setActiveNav(activePanel.id);
      this.updatePanelHeights();

      if (shouldReplayReveals) {
        this.replayPanelReveals(activePanel);
      }

      if (shouldScroll) {
        this.scrollToPanel(activePanel);
      }

      return Promise.allSettled(panelTransitions);
    }

    setPanelOpenImmediately(panel, shouldOpen) {
      const content = panel.querySelector("[data-fitment-panel-content]");
      const transition = this.panelTransitions.get(panel);

      if (transition && content) {
        window.clearTimeout(transition.timeoutId);
        content.removeEventListener(
          "transitionend",
          transition.onTransitionEnd,
        );
        transition.resolve?.();
        this.panelTransitions.delete(panel);
      }

      panel.classList.remove("is-opening", "is-closing");
      panel.open = shouldOpen;
      content?.style.removeProperty("max-height");
      content?.style.removeProperty("opacity");

      return Promise.resolve();
    }

    closePanel(panel) {
      panel.classList.remove("is-active");
      panel
        .querySelector(".fitment-category-panel__icon")
        ?.classList.remove("is-active");
      return this.animatePanel(panel, false);
    }

    animatePanel(panel, shouldOpen) {
      const content = panel.querySelector("[data-fitment-panel-content]");
      if (!content) {
        panel.open = shouldOpen;
        return Promise.resolve();
      }

      const isOpening = panel.classList.contains("is-opening");
      const isClosing = panel.classList.contains("is-closing");
      const previousTransition = this.panelTransitions.get(panel);
      if (shouldOpen && isOpening) {
        return previousTransition?.promise || Promise.resolve();
      }
      if (!shouldOpen && isClosing) {
        return previousTransition?.promise || Promise.resolve();
      }
      if (shouldOpen && panel.open && !isClosing) return Promise.resolve();
      if (!shouldOpen && !panel.open && !isOpening) return Promise.resolve();

      if (previousTransition) {
        window.clearTimeout(previousTransition.timeoutId);
        content.removeEventListener(
          "transitionend",
          previousTransition.onTransitionEnd,
        );
        previousTransition.resolve();
        this.panelTransitions.delete(panel);
      }

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        panel.open = shouldOpen;
        panel.classList.remove("is-opening", "is-closing");
        return Promise.resolve();
      }

      const currentHeight = panel.open
        ? content.getBoundingClientRect().height
        : 0;
      const currentOpacity = panel.open
        ? Number.parseFloat(getComputedStyle(content).opacity) || 0
        : 0;
      if (shouldOpen && !panel.open) {
        panel.open = true;
      }

      panel.classList.toggle("is-opening", shouldOpen);
      panel.classList.toggle("is-closing", !shouldOpen);
      content.style.transition = "none";
      content.style.maxHeight = `${currentHeight}px`;
      content.style.opacity = `${currentOpacity}`;
      void content.offsetHeight;
      content.style.removeProperty("transition");

      let resolveTransition;
      const transitionPromise = new Promise((resolve) => {
        resolveTransition = resolve;
      });
      const transition = {
        shouldOpen,
        timeoutId: null,
        onTransitionEnd: null,
        promise: transitionPromise,
        resolve: resolveTransition,
      };

      const finish = () => {
        if (this.panelTransitions.get(panel) !== transition) return;

        window.clearTimeout(transition.timeoutId);
        content.removeEventListener(
          "transitionend",
          transition.onTransitionEnd,
        );
        panel.classList.remove("is-opening", "is-closing");

        if (!shouldOpen) {
          panel.open = false;
        }

        content.style.removeProperty("max-height");
        content.style.removeProperty("opacity");
        this.panelTransitions.delete(panel);
        this.updatePanelHeights();
        transition.resolve();
      };

      transition.onTransitionEnd = (event) => {
        if (event.target === content && event.propertyName === "max-height") {
          finish();
        }
      };
      content.addEventListener("transitionend", transition.onTransitionEnd);
      transition.timeoutId = window.setTimeout(finish, 420);
      this.panelTransitions.set(panel, transition);

      window.requestAnimationFrame(() => {
        if (this.panelTransitions.get(panel) !== transition) return;

        content.style.maxHeight = shouldOpen
          ? `${content.scrollHeight}px`
          : "0px";
        content.style.opacity = shouldOpen ? "1" : "0";
      });

      return transition.promise;
    }

    preservePanelPosition(
      panel,
      targetTop,
      collapsingHeight,
      panelTransition,
    ) {
      if (collapsingHeight <= 1) return;

      const positionToken = ++this.panelPositionToken;
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      if (reduceMotion) {
        window.requestAnimationFrame(() => {
          if (positionToken !== this.panelPositionToken) return;
          const delta = panel.getBoundingClientRect().top - targetTop;
          if (Math.abs(delta) > 1) {
            window.scrollBy({ top: delta, left: 0, behavior: "auto" });
          }
        });
        return;
      }

      this.panelPositionTarget = { panel, targetTop };
      this.root.classList.add("is-switching-panels");

      let lastScrollY = window.scrollY;
      const followPanel = () => {
        if (positionToken !== this.panelPositionToken) return;
        const panelDocumentTop =
          panel.getBoundingClientRect().top + window.scrollY;
        const desiredScrollY = Math.max(0, panelDocumentTop - targetTop);
        // The target only moves upward while preceding panels close. Keeping
        // the absolute scroll position monotonic prevents Safari scroll
        // anchoring from bouncing the viewport between two positions.
        lastScrollY = Math.min(lastScrollY, desiredScrollY);
        window.scrollTo(0, lastScrollY);
        this.panelScrollFrame = window.requestAnimationFrame(followPanel);
      };
      this.panelScrollFrame = window.requestAnimationFrame(followPanel);

      Promise.resolve(panelTransition).then(() => {
        if (positionToken !== this.panelPositionToken) return;
        if (this.panelScrollFrame) {
          window.cancelAnimationFrame(this.panelScrollFrame);
          this.panelScrollFrame = null;
        }
        const correctSettledPosition = () => {
          const settledDelta = panel.getBoundingClientRect().top - targetTop;
          if (Math.abs(settledDelta) > 2) {
            window.scrollBy({
              top: settledDelta,
              left: 0,
              behavior: "auto",
            });
          }
        };

        // Closing the details element removes its final summary padding. Fix
        // that small last offset before the browser paints the settled state.
        correctSettledPosition();
        window.requestAnimationFrame(() => {
          if (positionToken !== this.panelPositionToken) return;
          correctSettledPosition();
          this.panelPositionTarget = null;
          this.root.classList.remove("is-switching-panels");
          this.updateStickyNavOffset();
        });
      });
    }

    finishPanelPositioning() {
      this.panelPositionToken += 1;
      if (this.panelScrollFrame) {
        window.cancelAnimationFrame(this.panelScrollFrame);
        this.panelScrollFrame = null;
      }

      const target = this.panelPositionTarget;
      const currentDelta = target
        ? target.panel.getBoundingClientRect().top - target.targetTop
        : 0;
      if (Math.abs(currentDelta) > 2) {
        window.scrollBy({
          top: currentDelta,
          left: 0,
          behavior: "auto",
        });
      }

      this.panelPositionTarget = null;
      this.root.classList.remove("is-switching-panels");
      this.updateStickyNavOffset();
    }

    bindStickyNavOffset() {
      const header = document.getElementById("theme-header");
      const scheduleUpdate = () => {
        if (this.root.classList.contains("is-switching-panels")) return;
        if (this.stickyOffsetFrame) return;
        this.stickyOffsetFrame = window.requestAnimationFrame(() => {
          this.stickyOffsetFrame = null;
          this.updateStickyNavOffset();
        });
      };

      const syncAfterLayout = () => {
        scheduleUpdate();
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(scheduleUpdate);
        });
      };

      this.updateStickyNavOffset();
      this.updatePanelHeights();
      window.addEventListener("scroll", scheduleUpdate, { passive: true });
      window.addEventListener("resize", () => {
        scheduleUpdate();
        this.updatePanelHeights();
      });
      window.addEventListener(
        "load",
        () => {
          syncAfterLayout();
          this.updatePanelHeights();
        },
        { once: true },
      );
      window.addEventListener("pageshow", syncAfterLayout);

      if (header && "ResizeObserver" in window) {
        this.headerResizeObserver = new ResizeObserver(scheduleUpdate);
        this.headerResizeObserver.observe(header);
      }

      if (header && "MutationObserver" in window) {
        this.headerMutationObserver = new MutationObserver(syncAfterLayout);
        this.headerMutationObserver.observe(header, {
          attributes: true,
          attributeFilter: ["class", "style"],
        });
      }

      document.fonts?.ready.then(syncAfterLayout);
    }

    bindScrollReveal() {
      const revealItems = [
        ...this.root.querySelectorAll("[data-fitment-reveal]"),
      ];
      if (!revealItems.length) return;

      if (!("IntersectionObserver" in window)) {
        revealItems.forEach((item) => item.classList.add("is-revealed"));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            this.revealChildren(entry.target);
            this.revealObserver.unobserve(entry.target);
          });
        },
        {
          rootMargin: "0px 0px -12% 0px",
          threshold: 0.18,
        },
      );

      revealItems.forEach((item) => this.revealObserver.observe(item));
    }

    updatePanelHeights() {
      window.requestAnimationFrame(() => {
        this.panels.forEach((panel) => {
          const content = panel.querySelector("[data-fitment-panel-content]");
          if (!content) return;
          panel.style.setProperty(
            "--fitment-panel-content-height",
            `${content.scrollHeight}px`,
          );
        });
      });
    }

    updateStickyNavOffset() {
      const header = document.getElementById("theme-header");
      const headerBottom = header
        ? Math.max(0, header.getBoundingClientRect().bottom)
        : 0;
      const gap = window.matchMedia("(max-width: 991px)").matches ? 0 : 12;
      const stickyTop = Math.round(headerBottom + gap);
      this.root.style.setProperty(
        "--fitment-category-nav-top",
        `${stickyTop}px`,
      );

      this.categoryNavigation?.updateStickyState(stickyTop);
    }

    setActiveNav(panelId) {
      this.categoryNavigation?.setActive(panelId);
    }

    triggerPanelIconAnimation(panel) {
      const panelIcon = panel.querySelector(
        ".fitment-category-panel__icon .fitment-category-icon",
      );
      if (!panelIcon) return;

      panelIcon.classList.remove("is-animating");
      void panelIcon.offsetWidth;
      panelIcon.classList.add("is-animating");
      window.setTimeout(() => {
        panelIcon.classList.remove("is-animating");
      }, 1350);
    }

    async scrollToPanel(panel) {
      let targetTop = this.getPanelScrollTarget(panel);
      window.scrollTo({ top: targetTop, behavior: "smooth" });
      await this.waitForScrollToSettle(targetTop);

      const settledTargetTop = this.getPanelScrollTarget(panel);
      if (Math.abs(window.scrollY - settledTargetTop) <= 2) return;

      targetTop = settledTargetTop;
      window.scrollTo({ top: targetTop, behavior: "smooth" });
      await this.waitForScrollToSettle(targetTop);
    }

    getPanelScrollTarget(panel, collapsingPanels = []) {
      this.updateStickyNavOffset();
      const navShell = this.root.querySelector(
        "[data-fitment-category-nav-shell]",
      );
      let stickyTop =
        parseFloat(
          this.root.style.getPropertyValue("--fitment-category-nav-top"),
        ) || 0;
      const panelDocumentTop =
        panel.getBoundingClientRect().top + window.scrollY;
      const header = document.getElementById("theme-header");

      if (
        header &&
        !header.classList.contains("header-sticky") &&
        panelDocumentTop > window.scrollY + header.offsetHeight
      ) {
        header.classList.add("header-sticky");
        const stickyGap = window.matchMedia("(max-width: 991px)").matches
          ? 0
          : 12;
        stickyTop =
          Math.max(0, header.getBoundingClientRect().bottom) + stickyGap;
        header.classList.remove("header-sticky");
      }

      const navHeight = navShell?.getBoundingClientRect().height || 0;
      const mobileProgress = navShell?.querySelector(
        "[data-parts-category-nav-progress]",
      );
      const isMobileProgressVisible =
        mobileProgress && getComputedStyle(mobileProgress).display !== "none";
      const panelGap = isMobileProgressVisible ? 0 : 18;
      const offset = stickyTop + navHeight + panelGap;
      const collapsingHeight = collapsingPanels.reduce(
        (total, collapsingPanel) => {
          const isBeforeTarget = Boolean(
            collapsingPanel.compareDocumentPosition(panel) &
            Node.DOCUMENT_POSITION_FOLLOWING,
          );
          if (!isBeforeTarget) return total;

          const content = collapsingPanel.querySelector(
            "[data-fitment-panel-content]",
          );
          return total + (content?.getBoundingClientRect().height || 0);
        },
        0,
      );
      const top = panelDocumentTop - offset - collapsingHeight;

      return Math.max(0, top);
    }

    waitForScrollToSettle(targetTop) {
      return new Promise((resolve) => {
        const start = window.performance.now();
        const maxDuration = 1200;
        let stableFrames = 0;
        let lastScrollY = window.scrollY;

        const check = (timestamp) => {
          const currentScrollY = window.scrollY;
          const distance = Math.abs(currentScrollY - targetTop);
          const movement = Math.abs(currentScrollY - lastScrollY);

          if (distance < 3 && movement < 1) {
            stableFrames += 1;
          } else {
            stableFrames = 0;
          }

          lastScrollY = currentScrollY;

          if (stableFrames >= 3 || timestamp - start > maxDuration) {
            resolve();
            return;
          }

          window.requestAnimationFrame(check);
        };

        window.requestAnimationFrame(check);
      });
    }

    revealChildren(container) {
      const children = [
        ...container.querySelectorAll(
          ".fitment-subcategory-card, .fitment-product-card",
        ),
      ];
      children.forEach((child, index) => {
        child.style.setProperty(
          "--fitment-reveal-delay",
          `${Math.min(index * 55, 420)}ms`,
        );
        child.classList.add("is-revealed");
      });
    }

    async replayPanelReveals(panel) {
      const revealItems = [...panel.querySelectorAll("[data-fitment-reveal]")];
      const revealToken = {};
      this.panelRevealTokens.set(panel, revealToken);

      revealItems.forEach((item) => {
        item.classList.remove("is-revealed");
        item
          .querySelectorAll(".fitment-subcategory-card, .fitment-product-card")
          .forEach((child) => child.classList.remove("is-revealed"));
      });

      await this.preparePanelImages(panel, "auto");
      if (this.panelRevealTokens.get(panel) !== revealToken) return;
      if (!panel.open || panel.classList.contains("is-closing")) return;

      window.requestAnimationFrame(() => {
        if (this.panelRevealTokens.get(panel) !== revealToken) return;
        if (!panel.open || panel.classList.contains("is-closing")) return;

        revealItems.forEach((item) => {
          item.classList.add("is-revealed");
          this.revealChildren(item);
        });
      });
    }

    preparePanelImages(panel, priority = "auto") {
      if (!panel) return Promise.resolve();

      const images = [
        ...panel.querySelectorAll(".fitment-subcategory-card__image img"),
      ];
      return this.prepareImages(images, priority);
    }

    prepareImages(images, priority = "auto", waitForAll = false) {
      if (!images.length) return Promise.resolve();

      const decodeImages = Promise.allSettled(
        images.map((image) => {
          image.loading = "eager";
          image.fetchPriority = priority;

          const existingPromise = this.imageDecodePromises.get(image);
          if (existingPromise) return existingPromise;

          let imagePromise;
          if (typeof image.decode === "function") {
            imagePromise = image.decode().catch(() => undefined);
          } else if (image.complete) {
            imagePromise = Promise.resolve();
          } else {
            imagePromise = new Promise((resolve) => {
              image.addEventListener("load", resolve, { once: true });
              image.addEventListener("error", resolve, { once: true });
            });
          }

          this.imageDecodePromises.set(image, imagePromise);
          return imagePromise;
        }),
      );

      if (waitForAll) return decodeImages;

      return Promise.race([
        decodeImages,
        new Promise((resolve) => window.setTimeout(resolve, 900)),
      ]);
    }

    escapeAlgoliaFilterValue(value) {
      return String(value ?? "")
        .replaceAll("\\", "\\\\")
        .replaceAll('"', '\\"');
    }

    escapeSelector(value) {
      if (window.CSS?.escape) {
        return CSS.escape(value);
      }

      return String(value).replaceAll('"', '\\"').replaceAll("\\", "\\\\");
    }

    findPanel(panelId) {
      if (!panelId) return null;
      return (
        this.root.querySelector(`#${this.escapeSelector(panelId)}`) ||
        document.getElementById(panelId)
      );
    }

    isTruthyFlag(value) {
      return value === true || value === 1 || value === "1" || value === "true";
    }

  }

  function initFitmentCollectionRedesign() {
    document
      .querySelectorAll("[data-fitment-collection-redesign]")
      .forEach((root) => {
        if (root.__fitmentCollectionRedesign) return;
        root.__fitmentCollectionRedesign = new FitmentCollectionRedesign(root);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initFitmentCollectionRedesign,
      { once: true },
    );
  } else {
    initFitmentCollectionRedesign();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.matches?.("[data-fitment-collection-redesign]")) {
      initFitmentCollectionRedesign();
      return;
    }

    if (event.target?.querySelector?.("[data-fitment-collection-redesign]")) {
      initFitmentCollectionRedesign();
    }
  });
})();
